import React, {Component} from 'react';
import PropTypes from 'prop-types';

import * as THREE from "three";

import STLLoader from "./utils/STLLoader";
import OrbitControls from "./utils/OrbitControls";
import Detector from "./utils/Detector";
import {Backend, Imprint, MapData} from "../Legal/Legal";

import {DimensionsWrapper, LegalWrapper, ThreeContainer, NoModelWarning} from "./Three.sc";

class Three extends Component {

    constructor(props) {
        super(props);

        if (!Detector.webgl) {
            Detector.addGetWebGLMessage();
        }

        this.scene = new THREE.Scene();
        this.loader = new STLLoader();

        this.state = {
            width: 0,
            height: 0
        };

        this.init = this.init.bind(this);
        this.onWindowResize = this.onWindowResize.bind(this);
        this.addObject = this.addObject.bind(this);
        this.removeObject = this.removeObject.bind(this);
        this.animate = this.animate.bind(this);
        this.renderModel = this.renderModel.bind(this);
    }

    componentDidMount() {
        this.init(this.el);
        this.animate();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {model} = this.props;

        if (model && prevProps.model !== model) {
            this.addObject(new STLLoader().parse(model));
        }
    }

    render() {
        const {model, showLegalWrapper} = this.props;

        const {width, height} = this.state;

        return (
            <ThreeContainer
                innerRef={el => this.el = el}
            >
                {
                    showLegalWrapper &&
                    <LegalWrapper>
                        <Backend/>
                        <span> &middot; </span>
                        <MapData/>
                        <span> &middot; </span>
                        <Imprint/>
                    </LegalWrapper>
                }
                {
                    model ?
                        <DimensionsWrapper>{`Dimensions: ${width.toFixed(1)}mm x ${height.toFixed(1)}mm`}</DimensionsWrapper> :
                        <NoModelWarning>You haven't created any model yet!</NoModelWarning>
                }
            </ThreeContainer>
        );
    }

    /* -------- THREE JS RELATED STUFF -------- */

    init(container) {
        this.rect = container.getBoundingClientRect();

        // Camera
        this.camera = new THREE.PerspectiveCamera(37.8, this.rect.width / this.rect.height, 1, 100000);

        // Controls
        this.controls = new OrbitControls(this.camera, container);
        this.controls.rotateSpeed = 1.0;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.noZoom = false;
        this.controls.noPan = false;
        this.controls.staticMoving = true;
        this.controls.dynamicDampingFactor = 0.3;
        this.controls.keys = [65, 83, 68];
        this.controls.addEventListener('change', this.renderModel);
        //this.controls.maxPolarAngle = Math.PI;
        //this.controls.autoRotate = true;

        // Background
        this.scene.background = new THREE.Color(0xF2F2F2);

        // Lights
        const light1 = new THREE.DirectionalLight(0xffffff);
        light1.position.set(1, 1, 1);
        this.scene.add(light1);
        const light2 = new THREE.DirectionalLight(0x002288);
        light2.position.set(-1, -1, -1);
        this.scene.add(light2);
        const light3 = new THREE.AmbientLight(0x222222);
        this.scene.add(light3);

        // Renderer
        this.renderer = new THREE.WebGLRenderer();
        this.renderer.setSize(this.rect.width, this.rect.height);
        container.appendChild(this.renderer.domElement);

        // Stats
        //this.stats = new Stats();
        //container.appendChild(this.stats.domElement);

        window.addEventListener('resize', this.onWindowResize, false);

        this.renderModel();
    }

    onWindowResize() {
        if (this.el) {
            this.rect = this.el.getBoundingClientRect();
            this.camera.aspect = this.rect.width / this.rect.height;
            this.camera.updateProjectionMatrix();

            this.renderer.setSize(this.rect.width, this.rect.height);

            this.renderModel();
        }
    }

    addObject(geometry) {
        // Remove any existing object
        this.removeObject();

        const material = new THREE.MeshPhongMaterial({
            color: 0x0D8DFF,
            specular: 0x000000,
            shininess: 20
        });

        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.geometry.computeBoundingBox();

        this.setState({
            width: Math.abs(this.mesh.geometry.boundingBox.min.x) + Math.abs(this.mesh.geometry.boundingBox.max.x) + 1,
            height: Math.abs(this.mesh.geometry.boundingBox.min.y) + Math.abs(this.mesh.geometry.boundingBox.max.y) + 1
        });

        this.mesh.geometry.center();
        this.mesh.geometry.computeBoundingSphere();
        this.mesh.rotation.x = -Math.PI / 2;

        this.mesh.rotation.z = Math.PI / 2;

        const radius = this.mesh.geometry.boundingSphere.radius;

        this.scene.add(this.mesh);
        this.camera.position.set(2 * radius, 2 * radius, 0); // reset cam
        this.renderModel();
    }

    removeObject() {
        if (this.mesh) {
            this.scene.remove(this.mesh);
            this.renderModel();
        }
    }

    animate() {
        requestAnimationFrame(this.animate);
        this.controls.update();
    }

    renderModel() {
        this.renderer.render(this.scene, this.camera);
        //this.stats.update();
    }
}

Three.propTypes = {
    showLegalWrapper: PropTypes.bool.isRequired,

    model: PropTypes.object
};

export default Three;



// WEBPACK FOOTER //
// ./src/common/Three/Three.js